import api from "api";
import { CompanyCommon } from "api/entities/bases/companyCommon";
import { ContractWithPricesServiceDispatcherCompany } from "api/entities/contract";
import Button from "commons/Button/Button";
import CustomCssTransition from "commons/CustomCssTransition";
import { TitleProvider } from "commons/DataManagement";
import { sessionStorageHelper } from "commons/Helpers/sessionStorageHelper";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "styles/UserListing.module.css";
import SecondaryRow from "./SecondaryRow";
import { LabelOrderElement } from "commons/DataManagement/DataList";

interface ContractsFilters {
  company_name: string;
  company_nip: string;
}

enum ContractOrder {
  COMPANY_NAME_DESC = "COMPANY_NAME_DESC",
  COMPANY_NAME_ASC = "COMPANY_NAME_ASC",
  COMPANY_NIP_DESC = "COMPANY_NIP_DESC",
  COMPANY_NIP_ASC = "COMPANY_NIP_ASC",
}

export default function Contracts() {
  const { t } = useTranslation("dispatcherView", { keyPrefix: "contracts" });

  const [audContracts, setAudContracts] = useState<ContractWithPricesServiceDispatcherCompany[]>([]);
  const [contractsFilter, setContractsFilter] = useState<ContractsFilters>({
    company_name: "",
    company_nip: "",
  });
  const [contractsOrder, setContractsOrder] = useState<ContractOrder>(ContractOrder.COMPANY_NAME_DESC);

  const getOperatorContracts = () => {
    async function getData() {
      api
        .fetchContractsByAuditorCoWithPricesDispatcherCompany(sessionStorageHelper.getDispatcherCoId(), {
          additional_query: {
            order_by: contractsOrder,
            company_name: contractsFilter.company_name,
            company_nip: contractsFilter.company_nip,
          },
        })
        .then((res) => {
          setAudContracts(res.data);
        });
    }
    getData();
  };

  const refresh = () => {
    getOperatorContracts();
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line
  }, [contractsFilter, contractsOrder]);

  return (
    <>
      <TitleProvider title={`${t("title")} | Raily Marketplace`} />
      <div className={styles.content}>
        <div className={styles.content_row}>
          <h1 className={styles.h1}>{t("title")}</h1>
        </div>
        <div className={styles.content_row}>
          <div className={styles.row_items}>
            <Button click={refresh} label={t("refresh")} />
          </div>
        </div>
        <div className={styles.table_wrapper}>
          <table className={styles.table_contracts}>
            <thead className={styles.thead}>
              <tr>
                <th scope="col" className={styles.th}>
                  <div className={styles.labelWrapper}>
                    <input
                      className={styles.input}
                      onChange={(event) => {
                        setContractsFilter({ ...contractsFilter, company_name: event.currentTarget.value });
                      }}
                      value={contractsFilter.company_name}
                      placeholder={t("coNameTrans")!}
                    />
                    <LabelOrderElement
                      orderable
                      descending={
                        [ContractOrder.COMPANY_NAME_DESC, ContractOrder.COMPANY_NAME_ASC].includes(contractsOrder)
                          ? contractsOrder === ContractOrder.COMPANY_NAME_DESC
                          : undefined
                      }
                      onClick={() => {
                        if (contractsOrder === ContractOrder.COMPANY_NAME_DESC) {
                          setContractsOrder(ContractOrder.COMPANY_NAME_ASC);
                        } else {
                          setContractsOrder(ContractOrder.COMPANY_NAME_DESC);
                        }
                      }}
                    />
                  </div>
                </th>
                <th scope="col" className={styles.th}>
                  <div className={styles.labelWrapper}>
                    <input
                      className={styles.input}
                      onChange={(event) => {
                        setContractsFilter({ ...contractsFilter, company_nip: event.currentTarget.value });
                      }}
                      value={contractsFilter.company_nip}
                      placeholder={t("taxNo")!}
                    />
                    <LabelOrderElement
                      orderable
                      descending={
                        [ContractOrder.COMPANY_NIP_DESC, ContractOrder.COMPANY_NIP_ASC].includes(contractsOrder)
                          ? contractsOrder === ContractOrder.COMPANY_NIP_DESC
                          : undefined
                      }
                      onClick={() => {
                        if (contractsOrder === ContractOrder.COMPANY_NIP_DESC) {
                          setContractsOrder(ContractOrder.COMPANY_NIP_ASC);
                        } else {
                          setContractsOrder(ContractOrder.COMPANY_NIP_DESC);
                        }
                      }}
                    />
                  </div>
                </th>
                <th scope="col" className={styles.th}>
                  {t("regon")}
                </th>
                <th scope="col" className={styles.th}>
                  {t("krs")}
                </th>
                <th scope="col" className={styles.th}>
                  {t("currency")}
                </th>
              </tr>
            </thead>
            <tbody>
              {audContracts.map((x) => {
                return (
                  <Row
                    key={x.id}
                    contract_id={x.id}
                    company={{
                      id: x.dispatcher_co_id,
                      tax_id: x.dispatcher_company.tax_id,
                      regon: x.dispatcher_company.regon,
                      krs_no: x.dispatcher_company.krs_no,
                      company_name: x.dispatcher_company.company_name,
                      address_id: x.dispatcher_company.address_id,
                    }}
                    currency={x.currency}
                    editMode={true}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Row(props: { company: CompanyCommon; contract_id: string; currency: string; editMode: boolean }) {
  const [expand, setExpand] = useState(false);

  return (
    <>
      <tr className={styles.tr} onClick={() => setExpand(!expand)}>
        {/* Primary row */}
        <PrimaryRow data={props.company} currecy={props.currency} />
      </tr>

      {/* Secondary expandable row */}
      <CustomCssTransition in={expand} trMode={true} colSpan={5}>
        <SecondaryRow
          contract_id={props.contract_id}
          expand={expand}
          currency={props.currency}
          company={props.company}
          editMode={props.editMode}
        />
      </CustomCssTransition>
    </>
  );
}

function PrimaryRow(props: { data: CompanyCommon; currecy: string }) {
  return (
    <>
      <td className={styles.td}>{props.data.company_name}</td>
      <td className={styles.td}>{props.data.tax_id}</td>
      <td className={styles.td}>{props.data.krs_no}</td>
      <td className={styles.td}>{props.data.regon}</td>
      <td className={styles.td}>{props.currecy}</td>
    </>
  );
}
